import React, { useState, useRef, useEffect } from "react";
import { Doughnut, Bar, Line } from "react-chartjs-2";
import article from "./StopLossContent";
import WaldoAd from '../AdComponent';
import { 
  Chart, 
  ArcElement, 
  Tooltip, 
  Legend, 
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Filler,
  BarElement
} from "chart.js";
import './PagesCss/StockProfitCalculator.css';

Chart.register(
  ArcElement, 
  Tooltip, 
  Legend, 
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Filler,
  BarElement
);

const MaxLossCalculator = () => {
  const content = article.find((item) => item.id === 6);
  const [activeTab, setActiveTab] = useState("calculator");
  const [isMobile, setIsMobile] = useState(false);
  const [savedCalculations, setSavedCalculations] = useState([]);
  const [showSaved, setShowSaved] = useState(false);
  const [strategyType, setStrategyType] = useState("long");
  const [notifications, setNotifications] = useState([]);
  const [calculationHistory, setCalculationHistory] = useState([]);

  const [inputs, setInputs] = useState({
    // Account Information
    accountSize: "50000",
    maxPortfolioLoss: "10",
    riskTolerance: "moderate",
    
    // Position Details
    entryPrice: "100.00",
    stopLossPrice: "90.00",
    positionSize: "5000",
    shares: "50",
    
    // Risk Parameters
    maxSingleLoss: "2",
    volatilityAdjustment: "1.0",
    correlationFactor: "0.3",
    blackSwanProtection: "5",
    
    // Portfolio Context
    currentPortfolioRisk: "3",
    openPositions: "3",
    maxPositions: "8",
    
    // Market Conditions
    marketVolatility: "Normal",
    trendDirection: "Bullish",
    supportLevel: "85.00",
    resistanceLevel: "110.00",
    
    // Advanced Settings
    marginUsed: "0",
    leverage: "1",
    commission: "10.00",
    slippage: "0.25"
  });

  const [lossAnalysis, setLossAnalysis] = useState(null);
  const [portfolioImpact, setPortfolioImpact] = useState(null);
  const chartRef = useRef();

  // Check mobile viewport
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Auto-calculate when inputs change
  useEffect(() => {
    if (activeTab === "calculator" || activeTab === "portfolio") {
      calculateMaxLoss();
    }
  }, [inputs, activeTab]);

  const addNotification = (message, type = "info") => {
    const id = Date.now();
    setNotifications(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      setNotifications(prev => prev.filter(notif => notif.id !== id));
    }, 5000);
  };

  const handleChange = (e) => {
    setInputs({ ...inputs, [e.target.name]: e.target.value });
  };

  const calculateMaxLoss = () => {
    const {
      accountSize,
      maxPortfolioLoss,
      entryPrice,
      stopLossPrice,
      positionSize,
      shares,
      maxSingleLoss,
      volatilityAdjustment,
      correlationFactor,
      blackSwanProtection,
      currentPortfolioRisk,
      openPositions,
      maxPositions,
      marginUsed,
      leverage,
      commission,
      slippage
    } = inputs;

    if (!accountSize || !entryPrice || !stopLossPrice) {
      return;
    }

    const account = parseFloat(accountSize);
    const maxPortfolioLossPercent = parseFloat(maxPortfolioLoss) / 100;
    const entry = parseFloat(entryPrice);
    const stopLoss = parseFloat(stopLossPrice);
    const position = parseFloat(positionSize) || parseFloat(shares) * entry;
    const maxSingleLossPercent = parseFloat(maxSingleLoss) / 100;
    const volAdjust = parseFloat(volatilityAdjustment) || 1.0;
    const correlation = parseFloat(correlationFactor) || 0.3;
    const blackSwanBuffer = parseFloat(blackSwanProtection) / 100;
    const currentRisk = parseFloat(currentPortfolioRisk) / 100;
    const openPos = parseInt(openPositions) || 0;
    const maxPos = parseInt(maxPositions) || 8;
    const margin = parseFloat(marginUsed) || 0;
    const lev = parseFloat(leverage) || 1;
    const comm = parseFloat(commission) || 0;
    const slip = parseFloat(slippage) || 0;

    // Calculate basic loss metrics
    const priceRiskPerShare = Math.abs(entry - stopLoss);
    const sharesHeld = position / entry;
    const maxLossPrice = priceRiskPerShare * sharesHeld;
    const maxLossPercent = (maxLossPrice / position) * 100;

    // Adjust for costs
    const totalCosts = comm * 2 + (slip * sharesHeld * 2);
    const adjustedMaxLoss = maxLossPrice + totalCosts;
    const adjustedMaxLossPercent = (adjustedMaxLoss / position) * 100;

    // Apply leverage and margin
    const leveragedLoss = adjustedMaxLoss * lev;
    const marginImpact = margin > 0 ? leveragedLoss / margin : 0;

    // Calculate risk-adjusted maximum loss
    const volatilityAdjustedLoss = leveragedLoss * volAdjust;
    const correlationAdjustedLoss = volatilityAdjustedLoss * (1 + correlation * openPos);
    const blackSwanAdjustedLoss = correlationAdjustedLoss * (1 + blackSwanBuffer);

    // Portfolio impact calculations
    const portfolioMaxLoss = account * maxPortfolioLossPercent;
    const currentPortfolioLoss = account * currentRisk;
    const availablePortfolioLoss = portfolioMaxLoss - currentPortfolioLoss;
    const portfolioLossUtilization = (currentPortfolioLoss / portfolioMaxLoss) * 100;

    // Position sizing validation
    const maxAllowedBySingleLoss = account * maxSingleLossPercent;
    const maxAllowedByPortfolio = availablePortfolioLoss / (1 + correlation * openPos);
    const optimalMaxLoss = Math.min(maxAllowedBySingleLoss, maxAllowedByPortfolio);

    // Risk scores and recommendations
    const singleLossScore = (leveragedLoss / account) * 100;
    const portfolioLossScore = (leveragedLoss / availablePortfolioLoss) * 100;
    const overallRiskScore = Math.max(singleLossScore, portfolioLossScore);

    // Calculate margin safety
    const marginCallPrice = margin > 0 ? entry * (1 - (1 / lev) * 0.75) : 0;
    const marginSafetyBuffer = marginCallPrice > 0 ? ((entry - marginCallPrice) / entry) * 100 : 0;

    const analysis = {
      basic: {
        priceRiskPerShare: priceRiskPerShare,
        shares: sharesHeld,
        maxLossPrice: maxLossPrice,
        maxLossPercent: maxLossPercent,
        totalCosts: totalCosts
      },
      adjusted: {
        adjustedMaxLoss: adjustedMaxLoss,
        adjustedMaxLossPercent: adjustedMaxLossPercent,
        leveragedLoss: leveragedLoss,
        leveragedLossPercent: (leveragedLoss / account) * 100,
        volatilityAdjustedLoss: volatilityAdjustedLoss,
        correlationAdjustedLoss: correlationAdjustedLoss,
        blackSwanAdjustedLoss: blackSwanAdjustedLoss
      },
      portfolio: {
        portfolioMaxLoss: portfolioMaxLoss,
        currentPortfolioLoss: currentPortfolioLoss,
        availablePortfolioLoss: availablePortfolioLoss,
        portfolioLossUtilization: portfolioLossUtilization,
        maxAllowedBySingleLoss: maxAllowedBySingleLoss,
        maxAllowedByPortfolio: maxAllowedByPortfolio,
        optimalMaxLoss: optimalMaxLoss
      },
      risk: {
        singleLossScore: singleLossScore,
        portfolioLossScore: portfolioLossScore,
        overallRiskScore: overallRiskScore,
        riskLevel: getRiskLevel(overallRiskScore),
        isWithinLimits: leveragedLoss <= optimalMaxLoss,
        marginCallPrice: marginCallPrice,
        marginSafetyBuffer: marginSafetyBuffer
      },
      metrics: {
        accountSize: account,
        positionSize: position,
        leverage: lev,
        maxSingleLossPercent: maxSingleLossPercent * 100,
        maxPortfolioLossPercent: maxPortfolioLossPercent * 100
      }
    };

    setLossAnalysis(analysis);
    calculatePortfolioImpact(analysis, account, openPos, maxPos);
  };

  const calculatePortfolioImpact = (analysis, accountSize, openPositions, maxPositions) => {
    const {
      adjusted,
      portfolio,
      risk
    } = analysis;

    const availableSlots = maxPositions - openPositions;
    const riskPerSlot = portfolio.portfolioMaxLoss / maxPositions;
    const currentRiskPerSlot = portfolio.currentPortfolioLoss / Math.max(openPositions, 1);
    const slotUtilization = (adjusted.leveragedLoss / riskPerSlot) * 100;

    const impact = {
      availableSlots: availableSlots,
      riskPerSlot: riskPerSlot,
      currentRiskPerSlot: currentRiskPerSlot,
      slotUtilization: slotUtilization,
      portfolioDiversity: (openPositions / maxPositions) * 100,
      concentrationRisk: (adjusted.leveragedLoss / accountSize) * 100,
      recommendedPositionSize: portfolio.optimalMaxLoss * 0.8, // 80% of optimal
      isOverConcentrated: slotUtilization > 100 || risk.overallRiskScore > 50
    };

    setPortfolioImpact(impact);
  };

  const getRiskLevel = (riskScore) => {
    if (riskScore <= 10) return { level: "Very Low", color: "#28A745", description: "Conservative" };
    if (riskScore <= 25) return { level: "Low", color: "#2B8C3A", description: "Moderate" };
    if (riskScore <= 50) return { level: "Medium", color: "#FFC107", description: "Balanced" };
    if (riskScore <= 75) return { level: "High", color: "#FF6B6B", description: "Aggressive" };
    return { level: "Extreme", color: "#E53E3E", description: "Dangerous" };
  };

  const generateRecommendations = (analysis, portfolioImpact) => {
    const recommendations = [];

    if (!analysis.risk.isWithinLimits) {
      recommendations.push({
        type: "danger",
        message: "Maximum loss exceeds risk limits",
        action: `Reduce position size to $${(portfolioImpact.recommendedPositionSize).toFixed(0)} or less`
      });
    }

    if (analysis.risk.overallRiskScore > 75) {
      recommendations.push({
        type: "danger",
        message: "Extreme risk level detected",
        action: "Consider not taking this trade or significantly reduce size"
      });
    }

    if (analysis.adjusted.leveragedLossPercent > analysis.metrics.maxSingleLossPercent) {
      recommendations.push({
        type: "warning",
        message: "Exceeds single trade loss limit",
        action: `Maximum single loss should be ${analysis.metrics.maxSingleLossPercent}% of account`
      });
    }

    if (portfolioImpact.slotUtilization > 100) {
      recommendations.push({
        type: "warning",
        message: "Over-utilizing portfolio risk slot",
        action: "Reduce position size to fit within allocated risk"
      });
    }

    if (analysis.risk.marginSafetyBuffer < 10 && analysis.metrics.leverage > 1) {
      recommendations.push({
        type: "warning",
        message: "Low margin safety buffer",
        action: "Consider reducing leverage or increasing stop loss distance"
      });
    }

    if (analysis.adjusted.blackSwanAdjustedLoss > analysis.portfolio.portfolioMaxLoss) {
      recommendations.push({
        type: "info",
        message: "Black swan scenario exceeds portfolio maximum",
        action: "Ensure you can withstand extreme market moves"
      });
    }

    if (recommendations.length === 0) {
      recommendations.push({
        type: "success",
        message: "Risk parameters within acceptable limits",
        action: "Proceed with trade as planned"
      });
    }

    return recommendations;
  };

  const calculateStopLossAdjustment = (analysis, desiredMaxLoss) => {
    const { basic, metrics } = analysis;
    const desiredDollarLoss = (desiredMaxLoss / 100) * metrics.accountSize;
    const requiredRiskPerShare = desiredDollarLoss / basic.shares;
    const newStopLoss = inputs.entryPrice - requiredRiskPerShare;
    
    return {
      newStopLoss: newStopLoss,
      stopLossAdjustment: ((inputs.stopLossPrice - newStopLoss) / inputs.stopLossPrice) * 100,
      requiredChange: requiredRiskPerShare - basic.priceRiskPerShare
    };
  };

  const resetCalculator = () => {
    setInputs({
      accountSize: "50000",
      maxPortfolioLoss: "10",
      riskTolerance: "moderate",
      entryPrice: "100.00",
      stopLossPrice: "90.00",
      positionSize: "5000",
      shares: "50",
      maxSingleLoss: "2",
      volatilityAdjustment: "1.0",
      correlationFactor: "0.3",
      blackSwanProtection: "5",
      currentPortfolioRisk: "3",
      openPositions: "3",
      maxPositions: "8",
      marketVolatility: "Normal",
      trendDirection: "Bullish",
      supportLevel: "85.00",
      resistanceLevel: "110.00",
      marginUsed: "0",
      leverage: "1",
      commission: "10.00",
      slippage: "0.25"
    });
    addNotification("Calculator reset to default values", "success");
  };

  const exportToCSV = () => {
    if (!lossAnalysis) return;
    
    const csvContent = [
      "Metric,Value",
      `Account Size,$${lossAnalysis.metrics.accountSize}`,
      `Position Size,$${lossAnalysis.metrics.positionSize}`,
      `Basic Maximum Loss,$${lossAnalysis.basic.maxLossPrice.toFixed(2)}`,
      `Basic Loss Percentage,${lossAnalysis.basic.maxLossPercent.toFixed(2)}%`,
      `Adjusted Maximum Loss,$${lossAnalysis.adjusted.adjustedMaxLoss.toFixed(2)}`,
      `Leveraged Loss,$${lossAnalysis.adjusted.leveragedLoss.toFixed(2)}`,
      `Leveraged Loss Percentage,${lossAnalysis.adjusted.leveragedLossPercent.toFixed(2)}%`,
      `Portfolio Maximum Loss,$${lossAnalysis.portfolio.portfolioMaxLoss.toFixed(2)}`,
      `Available Portfolio Loss,$${lossAnalysis.portfolio.availablePortfolioLoss.toFixed(2)}`,
      `Optimal Maximum Loss,$${lossAnalysis.portfolio.optimalMaxLoss.toFixed(2)}`,
      `Risk Score,${lossAnalysis.risk.overallRiskScore.toFixed(1)}`,
      `Risk Level,${lossAnalysis.risk.riskLevel.level}`,
      `Within Risk Limits,${lossAnalysis.risk.isWithinLimits ? 'Yes' : 'No'}`
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'maximum-loss-analysis.csv';
    a.click();
    window.URL.revokeObjectURL(url);
    addNotification("Analysis exported to CSV successfully", "success");
  };

  const printResult = () => {
    window.print();
  };

  const saveToHistory = () => {
    if (!lossAnalysis) return;
    
    const newCalculation = {
      id: Date.now(),
      timestamp: new Date().toLocaleString(),
      inputs: { ...inputs },
      analysis: lossAnalysis
    };
    
    setCalculationHistory(prev => [newCalculation, ...prev.slice(0, 9)]);
    addNotification("Analysis saved to history", "success");
  };

  const quickPreset = (preset) => {
    const presets = {
      conservative: { maxSingleLoss: "1", maxPortfolioLoss: "5", blackSwanProtection: "10" },
      moderate: { maxSingleLoss: "2", maxPortfolioLoss: "10", blackSwanProtection: "5" },
      aggressive: { maxSingleLoss: "3", maxPortfolioLoss: "15", blackSwanProtection: "2" }
    };
    
    setInputs(prev => ({ ...prev, ...presets[preset] }));
    addNotification(`${preset} risk preset applied`, "info");
  };

  const strategyPreset = (strategy) => {
    const presets = {
      long: { stopLossPrice: "90.00", leverage: "1", volatilityAdjustment: "1.0" },
      short: { stopLossPrice: "110.00", leverage: "1", volatilityAdjustment: "1.2" },
      swing: { stopLossPrice: "85.00", leverage: "1", volatilityAdjustment: "1.5" },
      day: { stopLossPrice: "95.00", leverage: "2", volatilityAdjustment: "2.0" }
    };
    
    setInputs(prev => ({ ...prev, ...presets[strategy] }));
    setStrategyType(strategy);
    addNotification(`${strategy} trading strategy applied`, "info");
  };

  // Chart Data
  const lossBreakdownData = lossAnalysis ? {
    labels: ["Basic Price Risk", "Trading Costs", "Volatility Adjustment", "Correlation Impact", "Black Swan Buffer"],
    datasets: [{
      data: [
        lossAnalysis.basic.maxLossPrice,
        lossAnalysis.basic.totalCosts,
        lossAnalysis.adjusted.volatilityAdjustedLoss - lossAnalysis.adjusted.adjustedMaxLoss,
        lossAnalysis.adjusted.correlationAdjustedLoss - lossAnalysis.adjusted.volatilityAdjustedLoss,
        lossAnalysis.adjusted.blackSwanAdjustedLoss - lossAnalysis.adjusted.correlationAdjustedLoss
      ],
      backgroundColor: [
        "#19692C",
        "#2B8C3A",
        "#FFC107",
        "#FF6B6B",
        "#E53E3E"
      ],
      borderWidth: 2,
      hoverOffset: 15
    }],
  } : null;

  const portfolioAllocationData = lossAnalysis ? {
    labels: ["Current Portfolio Risk", "This Trade Risk", "Available Risk Buffer", "Unused Risk Capacity"],
    datasets: [{
      data: [
        lossAnalysis.portfolio.currentPortfolioLoss,
        lossAnalysis.adjusted.leveragedLoss,
        Math.max(lossAnalysis.portfolio.availablePortfolioLoss - lossAnalysis.adjusted.leveragedLoss, 0),
        Math.max(lossAnalysis.portfolio.portfolioMaxLoss - lossAnalysis.portfolio.currentPortfolioLoss - lossAnalysis.adjusted.leveragedLoss, 0)
      ],
      backgroundColor: [
        "#FF6B6B",
        "#FFC107",
        "#28A745",
        "#E8F5EB"
      ],
      borderWidth: 2
    }]
  } : null;

  const riskComparisonData = lossAnalysis ? {
    labels: ["Basic Loss", "With Costs", "With Leverage", "Vol Adjusted", "Correlation Adjusted", "Black Swan Adjusted"],
    datasets: [{
      label: 'Maximum Loss ($)',
      data: [
        lossAnalysis.basic.maxLossPrice,
        lossAnalysis.adjusted.adjustedMaxLoss,
        lossAnalysis.adjusted.leveragedLoss,
        lossAnalysis.adjusted.volatilityAdjustedLoss,
        lossAnalysis.adjusted.correlationAdjustedLoss,
        lossAnalysis.adjusted.blackSwanAdjustedLoss
      ],
      backgroundColor: 'rgba(25, 105, 44, 0.6)',
      borderColor: '#19692C',
      borderWidth: 2
    }]
  } : null;

  const riskLevel = lossAnalysis ? lossAnalysis.risk.riskLevel : null;
  const recommendations = lossAnalysis && portfolioImpact ? 
    generateRecommendations(lossAnalysis, portfolioImpact) : [];

  return (
    <div className={'profit-calculator'}>
      {/* Notifications */}
      <div className="notifications-container">
        {notifications.map(notif => (
          <div key={notif.id} className={`notification ${notif.type}`}>
            <i className={`fas fa-${notif.type === 'success' ? 'check' : 'info'}-circle`}></i>
            {notif.message}
          </div>
        ))}
      </div>

      <div className="calculator-header">
        <div className="header-top">
          <h1>Maximum Loss Calculator</h1>
        </div>
        <p>Calculate worst-case scenario losses and protect your portfolio from catastrophic risk</p>
      </div>
<WaldoAd type="horizontal" /><br/>
      <div className="calculator-layout">
        {/* Left Side - Inputs */}
        <div className="left-panel">
          {/* Navigation Tabs */}
          <div className="tabs-container">
            <div className="tabs">
              <button 
                className={`tab ${activeTab === "calculator" ? "active" : ""}`}
                onClick={() => setActiveTab("calculator")}
              >
                <i className="fas fa-calculator"></i>
                {isMobile ? 'Calculator' : 'Loss Calculator'}
              </button>
              <button 
                className={`tab ${activeTab === "portfolio" ? "active" : ""}`}
                onClick={() => setActiveTab("portfolio")}
              >
                <i className="fas fa-chart-pie"></i>
                {isMobile ? 'Portfolio' : 'Portfolio Impact'}
              </button>
              <button 
                className={`tab ${activeTab === "adjustment" ? "active" : ""}`}
                onClick={() => setActiveTab("adjustment")}
              >
                <i className="fas fa-sliders-h"></i>
                {isMobile ? 'Adjust' : 'Risk Adjustment'}
              </button>
              <button 
                className={`tab ${activeTab === "history" ? "active" : ""}`}
                onClick={() => setActiveTab("history")}
              >
                <i className="fas fa-history"></i>
                {isMobile ? 'History' : 'History'}
              </button>
            </div>
          </div>

          {/* Risk Profile Presets */}
          <div className="presets-section">
            <h4>Risk Profile:</h4>
            <div className="preset-buttons">
              <button className="preset-btn conservative" onClick={() => quickPreset('conservative')}>
                Conservative
              </button>
              <button className="preset-btn moderate" onClick={() => quickPreset('moderate')}>
                Moderate
              </button>
              <button className="preset-btn aggressive" onClick={() => quickPreset('aggressive')}>
                Aggressive
              </button>
            </div>
          </div>

          {/* Strategy Selection */}
          <div className="mode-selection">
            <div className="mode-buttons">
              <button 
                className={`mode-btn ${strategyType === "long" ? "active" : ""}`}
                onClick={() => strategyPreset('long')}
              >
                <i className="fas fa-arrow-up"></i>
                Long Position
              </button>
              <button 
                className={`mode-btn ${strategyType === "short" ? "active" : ""}`}
                onClick={() => strategyPreset('short')}
              >
                <i className="fas fa-arrow-down"></i>
                Short Position
              </button>
              <button 
                className={`mode-btn ${strategyType === "swing" ? "active" : ""}`}
                onClick={() => strategyPreset('swing')}
              >
                <i className="fas fa-wave-square"></i>
                Swing Trade
              </button>
              <button 
                className={`mode-btn ${strategyType === "day" ? "active" : ""}`}
                onClick={() => strategyPreset('day')}
              >
                <i className="fas fa-bolt"></i>
                Day Trade
              </button>
            </div>
            <div className="mode-description">
              {strategyType === "long" 
                ? "Buying with expectation of price increase. Stop loss below entry."
                : strategyType === "short"
                ? "Selling with expectation of price decrease. Stop loss above entry."
                : strategyType === "swing"
                ? "Medium-term trades with wider stops for volatility absorption."
                : "Short-term trades with tight stops and potential for higher leverage."
              }
            </div>
          </div>

          {/* Loss Calculator Tab */}
          {activeTab === "calculator" && (
            <div className="calculator-content">
              <div className="input-section">
                <div className="input-group">
                  <h3>
                    <i className="fas fa-wallet"></i>
                    Account & Risk Limits
                  </h3>
                  <div className="input-row">
                    <div className="input-field">
                      <label><i className="fa-solid fa-dollar-sign"></i> Account Size</label>
                      <input 
                        type="number" 
                        name="accountSize" 
                        value={inputs.accountSize} 
                        onChange={handleChange}
                        placeholder="50000"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-shield-alt"></i> Max Portfolio Loss (%)</label>
                      <input 
                        type="number" 
                        name="maxPortfolioLoss" 
                        value={inputs.maxPortfolioLoss} 
                        onChange={handleChange}
                        placeholder="10"
                        step="0.1"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-exclamation-triangle"></i> Max Single Trade Loss (%)</label>
                      <input 
                        type="number" 
                        name="maxSingleLoss" 
                        value={inputs.maxSingleLoss} 
                        onChange={handleChange}
                        placeholder="2"
                        step="0.1"
                      />
                    </div>
                  </div>

                  <h3>
                    <i className="fas fa-chart-line"></i>
                    Position Details
                  </h3>
                  <div className="input-row">
                    <div className="input-field">
                      <label><i className="fa-solid fa-arrow-right"></i> Entry Price</label>
                      <input 
                        type="number" 
                        name="entryPrice" 
                        value={inputs.entryPrice} 
                        onChange={handleChange}
                        placeholder="100.00"
                        step="0.01"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-arrow-down"></i> Stop Loss Price</label>
                      <input 
                        type="number" 
                        name="stopLossPrice" 
                        value={inputs.stopLossPrice} 
                        onChange={handleChange}
                        placeholder="90.00"
                        step="0.01"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-dollar-sign"></i> Position Size</label>
                      <input 
                        type="number" 
                        name="positionSize" 
                        value={inputs.positionSize} 
                        onChange={handleChange}
                        placeholder="5000"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-layer-group"></i> Shares</label>
                      <input 
                        type="number" 
                        name="shares" 
                        value={inputs.shares} 
                        onChange={handleChange}
                        placeholder="50"
                      />
                    </div>
                  </div>

                  <h3>
                    <i className="fas fa-cogs"></i>
                    Advanced Risk Parameters
                  </h3>
                  <div className="input-row">
                    <div className="input-field">
                      <label><i className="fa-solid fa-wave-square"></i> Volatility Adjustment</label>
                      <select name="volatilityAdjustment" value={inputs.volatilityAdjustment} onChange={handleChange}>
                        <option value="0.5">Very Low (0.5x)</option>
                        <option value="0.8">Low (0.8x)</option>
                        <option value="1.0">Normal (1.0x)</option>
                        <option value="1.2">High (1.2x)</option>
                        <option value="1.5">Very High (1.5x)</option>
                        <option value="2.0">Extreme (2.0x)</option>
                      </select>
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-link"></i> Correlation Factor</label>
                      <select name="correlationFactor" value={inputs.correlationFactor} onChange={handleChange}>
                        <option value="0.1">Low (0.1)</option>
                        <option value="0.3">Medium (0.3)</option>
                        <option value="0.5">High (0.5)</option>
                        <option value="0.7">Very High (0.7)</option>
                      </select>
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-bolt"></i> Black Swan Protection (%)</label>
                      <input 
                        type="number" 
                        name="blackSwanProtection" 
                        value={inputs.blackSwanProtection} 
                        onChange={handleChange}
                        placeholder="5"
                        step="0.1"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="action-buttons">
                <button className="btn reset-btn" onClick={resetCalculator}>
                  <i className="fas fa-redo"></i>
                  Reset
                </button>
                <button className="btn save-btn" onClick={saveToHistory}>
                  <i className="fas fa-save"></i>
                  Save Analysis
                </button>
              </div>
            </div>
          )}
<WaldoAd type="square" />
          {/* Portfolio Impact Tab */}
          {activeTab === "portfolio" && (
            <div className="projection-content">
              <div className="input-section">
                <h3>
                  <i className="fas fa-chart-pie"></i>
                  Portfolio Context
                </h3>
                <div className="input-row">
                  <div className="input-field">
                    <label><i className="fa-solid fa-chart-line"></i> Portfolio Risk (%)</label>
                    <input 
                      type="number" 
                      name="currentPortfolioRisk" 
                      value={inputs.currentPortfolioRisk} 
                      onChange={handleChange}
                      placeholder="3"
                      step="0.1"
                    />
                  </div>
                  <div className="input-field">
                    <label><i className="fa-solid fa-layer-group"></i> Open Positions</label>
                    <input 
                      type="number" 
                      name="openPositions" 
                      value={inputs.openPositions} 
                      onChange={handleChange}
                      placeholder="3"
                    />
                  </div>
                  <div className="input-field">
                    <label><i className="fa-solid fa-flag"></i> Maximum Positions</label>
                    <input 
                      type="number" 
                      name="maxPositions" 
                      value={inputs.maxPositions} 
                      onChange={handleChange}
                      placeholder="8"
                    />
                  </div>
                </div><br/>

                <h3>
                  <i className="fas fa-sliders-h"></i>
                  Advanced Settings
                </h3>
                <div className="input-row">
                  <div className="input-field">
                    <label><i className="fa-solid fa-rocket"></i> Leverage</label>
                    <select name="leverage" value={inputs.leverage} onChange={handleChange}>
                      <option value="1">1x (No Leverage)</option>
                      <option value="2">2x</option>
                      <option value="3">3x</option>
                      <option value="5">5x</option>
                      <option value="10">10x</option>
                    </select>
                  </div>
                  <div className="input-field">
                    <label><i className="fa-solid fa-percentage"></i> Margin Required</label>
                    <input 
                      type="number" 
                      name="marginUsed" 
                      value={inputs.marginUsed} 
                      onChange={handleChange}
                      placeholder="0"
                      step="0.01"
                    />
                  </div>
                  <div className="input-field">
                    <label><i className="fa-solid fa-coins"></i> Commission per Trade</label>
                    <input 
                      type="number" 
                      name="commission" 
                      value={inputs.commission} 
                      onChange={handleChange}
                      placeholder="10.00"
                      step="0.01"
                    />
                  </div>
                  <div className="input-field">
                    <label><i className="fa-solid fa-arrows-left-right"></i> Slippage per Share</label>
                    <input 
                      type="number" 
                      name="slippage" 
                      value={inputs.slippage} 
                      onChange={handleChange}
                      placeholder="0.25"
                      step="0.01"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* History Tab */}
          {activeTab === "history" && (
            <div className="history-content">
              <h3>
                <i className="fas fa-history"></i>
                Loss Analysis History
              </h3>
              {calculationHistory.length === 0 ? (
                <div className="empty-state">
                  <i className="fas fa-inbox fa-3x"></i>
                  <p>No analyses yet. Start calculating to see your history here.</p>
                </div>
              ) : (
                <div className="history-list">
                  {calculationHistory.map(calc => (
                    <div key={calc.id} className="history-item">
                      <div className="history-header">
                        <span className="history-date">
                          <i className="fas fa-clock"></i>
                          {calc.timestamp}
                        </span>
                        <span className={`history-profit ${calc.analysis.risk.isWithinLimits ? 'positive' : 'negative'}`}>
                          <i className={`fas ${calc.analysis.risk.isWithinLimits ? 'fa-check' : 'fa-exclamation-triangle'}`}></i>
                          ${calc.analysis.adjusted.leveragedLoss.toFixed(0)}
                        </span> 
                      </div>
                      <div className="history-details">
                        <span><i className="fas fa-percentage"></i> Loss: {calc.analysis.adjusted.leveragedLossPercent.toFixed(1)}%</span>
                        <span><i className="fas fa-shield-alt"></i> Risk: {calc.analysis.risk.riskLevel.level}</span>
                        <span><i className="fas fa-balance-scale"></i> Within Limits: {calc.analysis.risk.isWithinLimits ? 'Yes' : 'No'}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right Side - Results */}
        {(activeTab === "calculator" || activeTab === "portfolio" || activeTab === "adjustment") && (
          <div className="right-panel">
            {activeTab === "calculator" && (
              <div className="results-section">
                <div className="results-header">
                  <h2>
                    <i className="fas fa-exclamation-triangle"></i>
                    Maximum Loss Analysis
                  </h2>
                  <div className="result-actions">
                    <button className="btn export-btn" onClick={exportToCSV}>
                      <i className="fas fa-file-export"></i>
                      {isMobile ? 'CSV' : 'Export CSV'}
                    </button>
                    <button className="btn print-btn" onClick={printResult}>
                      <i className="fas fa-print"></i>
                      {isMobile ? 'Print' : 'Print'}
                    </button>
                  </div>
                </div>
<WaldoAd type="square" />
                {lossAnalysis ? (
                  <>
                    {/* Loss Summary */}
                    <div className="results-grid">
                      <div className="result-card highlight">
                        <div className="result-value" style={{ color: '#FFFFFF' }}>
                          ${lossAnalysis.adjusted.leveragedLoss.toFixed(0)}
                        </div>
                        <div className="result-label">
                          <i className="fas fa-exclamation-triangle"></i>
                          Maximum Loss
                        </div>
                      </div>
                      <div className="result-card">
                        <div className="result-value" style={{ color: riskLevel?.color }}>
                          {lossAnalysis.adjusted.leveragedLossPercent.toFixed(2)}%
                        </div>
                        <div className="result-label">
                          <i className="fas fa-percentage"></i>
                          Account Impact
                        </div>
                      </div>
                      <div className="result-card">
                        <div className="result-value" style={{ color: riskLevel?.color }}>
                          {lossAnalysis.risk.overallRiskScore.toFixed(1)}
                        </div>
                        <div className="result-label">
                          <i className="fas fa-shield-alt"></i>
                          Risk Score
                        </div>
                      </div>
                      <div className="result-card">
                        <div className="result-value" style={{ color: riskLevel?.color }}>
                          {riskLevel?.level}
                        </div>
                        <div className="result-label">
                          <i className="fas fa-flag"></i>
                          Risk Level
                        </div>
                      </div>
                    </div>

                    {/* Risk Level Indicator */}
                    <div className="risk-summary">
                      <h4>Risk Assessment: <span style={{ color: riskLevel?.color }}>{riskLevel?.level} - {riskLevel?.description}</span></h4>
                      <div className="risk-meter">
                        <div className="meter-bar">
                          <div 
                            className="meter-fill"
                            style={{ 
                              width: `${Math.min(lossAnalysis.risk.overallRiskScore, 100)}%`,
                              backgroundColor: riskLevel?.color
                            }}
                          ></div>
                        </div>
                        <div className="meter-value" style={{ color: riskLevel?.color }}>
                          Score: {lossAnalysis.risk.overallRiskScore.toFixed(1)}/100
                        </div>
                      </div>
                    </div>

                    {/* Loss Breakdown */}
                    <div className="detailed-results">
                      <div className="detail-row">
                        <span><i className="fas fa-chart-line"></i> Basic Price Risk:</span>
                        <span>${lossAnalysis.basic.maxLossPrice.toFixed(2)} ({lossAnalysis.basic.maxLossPercent.toFixed(2)}%)</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-coins"></i> Trading Costs:</span>
                        <span>+${lossAnalysis.basic.totalCosts.toFixed(2)}</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-wave-square"></i> Volatility Adjustment:</span>
                        <span>+${(lossAnalysis.adjusted.volatilityAdjustedLoss - lossAnalysis.adjusted.adjustedMaxLoss).toFixed(2)}</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-link"></i> Correlation Impact:</span>
                        <span>+${(lossAnalysis.adjusted.correlationAdjustedLoss - lossAnalysis.adjusted.volatilityAdjustedLoss).toFixed(2)}</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-bolt"></i> Black Swan Buffer:</span>
                        <span>+${(lossAnalysis.adjusted.blackSwanAdjustedLoss - lossAnalysis.adjusted.correlationAdjustedLoss).toFixed(2)}</span>
                      </div>
                    </div>

                    {/* Loss Breakdown Chart */}
                    <div className="chart-container">
                      <h3>
                        <i className="fas fa-chart-pie"></i>
                        Loss Composition
                      </h3>
                      <Doughnut 
                        data={lossBreakdownData} 
                        options={{
                          responsive: true,
                          maintainAspectRatio: true,
                          plugins: {
                            legend: {
                              position: isMobile ? 'top' : 'right'
                            },
                            tooltip: {
                              callbacks: {
                                label: function(context) {
                                  return `$${context.parsed.toFixed(2)}`;
                                }
                              }
                            }
                          }
                        }}
                      />
                    </div>

                    {/* Risk Comparison Chart */}
                    <div className="chart-container">
                      <h3>
                        <i className="fas fa-chart-bar"></i>
                        Loss Escalation Analysis
                      </h3>
                      <Bar 
                        data={riskComparisonData}
                        options={{
                          responsive: true,
                          maintainAspectRatio: true,
                          plugins: {
                            legend: {
                              display: false
                            }
                          },
                          scales: {
                            y: {
                              beginAtZero: true,
                              ticks: {
                                callback: function(value) {
                                  return '$' + value.toLocaleString(undefined, {maximumFractionDigits: 0});
                                }
                              }
                            }
                          }
                        }}
                      />
                    </div>
                  </>
                ) : (
                  <div className="empty-results">
                    <i className="fas fa-calculator fa-3x"></i>
                    <p>Enter position details to calculate maximum loss</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === "portfolio" && lossAnalysis && portfolioImpact && (
              <div className="projection-results">
                <h3>
                  <i className="fas fa-chart-pie"></i>
                  Portfolio Impact Analysis
                </h3>

                {/* Portfolio Allocation */}
                <div className="projection-summary">
                  <div className="summary-card">
                    <div className="summary-value" style={{ color: portfolioImpact.isOverConcentrated ? '#E53E3E' : '#28A745' }}>
                      {portfolioImpact.slotUtilization.toFixed(0)}%
                    </div>
                    <div className="summary-label">
                      <i className="fas fa-chart-pie"></i>
                      Risk Slot Utilization
                    </div>
                  </div>
                  <div className="summary-card">
                    <div className="summary-value">
                      {portfolioImpact.availableSlots}
                    </div>
                    <div className="summary-label">
                      <i className="fas fa-layer-group"></i>
                      Available Slots
                    </div>
                  </div>
                  <div className="summary-card">
                    <div className="summary-value" style={{ color: lossAnalysis.risk.isWithinLimits ? '#28A745' : '#E53E3E' }}>
                      {lossAnalysis.risk.isWithinLimits ? 'Yes' : 'No'}
                    </div>
                    <div className="summary-label">
                      <i className="fas fa-check-circle"></i>
                      Within Limits
                    </div>
                  </div>
                  <div className="summary-card">
                    <div className="summary-value">
                      ${portfolioImpact.recommendedPositionSize.toFixed(0)}
                    </div>
                    <div className="summary-label">
                      <i className="fas fa-bullseye"></i>
                      Recommended Size
                    </div>
                  </div>
                </div>

                {/* Portfolio Risk Allocation */}
                <div className="chart-container">
                  <h3>
                    <i className="fas fa-chart-pie"></i>
                    Portfolio Risk Allocation
                  </h3>
                  <Doughnut 
                    data={portfolioAllocationData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: true,
                      plugins: {
                        legend: {
                          position: isMobile ? 'top' : 'right'
                        },
                        tooltip: {
                          callbacks: {
                            label: function(context) {
                              return `$${context.parsed.toLocaleString(undefined, {maximumFractionDigits: 0})}`;
                            }
                          }
                        }
                      }
                    }}
                  />
                </div>

                {/* Recommendations */}
                <div className="risk-summary">
                  <h4>Risk Management Recommendations</h4>
                  <div className="scenario-list">
                    {recommendations.map((rec, index) => (
                      <div key={index} className="scenario-item">
                        <div className="scenario-name">
                          <i className={`fas fa-${rec.type === 'success' ? 'check' : rec.type === 'warning' ? 'exclamation-triangle' : 'exclamation-circle'} fa-xs`}></i>
                          {rec.message}
                        </div>
                        <div className="scenario-outcome">{rec.action}</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Margin Analysis */}
                {lossAnalysis.metrics.leverage > 1 && (
                  <div className="risk-summary">
                    <h4>Margin Safety Analysis</h4>
                    <div className="risk-metrics">
                      <div className="risk-metric">
                        <span className="metric-label">Margin Call Price:</span>
                        <span className="metric-value">${lossAnalysis.risk.marginCallPrice.toFixed(2)}</span>
                      </div>
                      <div className="risk-metric">
                        <span className="metric-label">Safety Buffer:</span>
                        <span className="metric-value" style={{ 
                          color: lossAnalysis.risk.marginSafetyBuffer > 15 ? '#28A745' : 
                                 lossAnalysis.risk.marginSafetyBuffer > 10 ? '#FFC107' : '#E53E3E'
                        }}>
                          {lossAnalysis.risk.marginSafetyBuffer.toFixed(1)}%
                        </span>
                      </div>
                      <div className="risk-metric">
                        <span className="metric-label">Leverage Used:</span>
                        <span className="metric-value">{lossAnalysis.metrics.leverage}x</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === "adjustment" && lossAnalysis && (
              <div className="risk-results-panel">
                <h3>
                  <i className="fas fa-sliders-h"></i>
                  Risk Adjustment Tools
                </h3><br/>
                
                {/* Stop Loss Adjustment Calculator */}
                <div className="scenario-comparison">
                  <div className="scenario-item">
                    <div className="scenario-name">Current Stop Loss Analysis</div>
                    <div className="scenario-stats">
                      <div className="breakdown-item">
                        <span>Current Stop Loss:</span>
                        <span>${inputs.stopLossPrice}</span>
                      </div>
                      <div className="breakdown-item">
                        <span>Price Risk per Share:</span>
                        <span>${lossAnalysis.basic.priceRiskPerShare.toFixed(2)}</span>
                      </div>
                      <div className="breakdown-item">
                        <span>Distance from Entry:</span>
                        <span>{((Math.abs(parseFloat(inputs.entryPrice) - parseFloat(inputs.stopLossPrice)) / parseFloat(inputs.entryPrice)) * 100).toFixed(1)}%</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Adjustment Scenarios */}
                  <div className="scenario-item">
                    <div className="scenario-name">Adjust to Single Loss Limit</div>
                    <div className="scenario-stats">
                      <div className="breakdown-item">
                        <span>Required Stop Loss:</span>
                        <span>${calculateStopLossAdjustment(lossAnalysis, parseFloat(inputs.maxSingleLoss)).newStopLoss.toFixed(2)}</span>
                      </div>
                      <div className="breakdown-item">
                        <span>Adjustment Needed:</span>
                        <span>{calculateStopLossAdjustment(lossAnalysis, parseFloat(inputs.maxSingleLoss)).stopLossAdjustment.toFixed(1)}%</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="scenario-item">
                    <div className="scenario-name">Adjust to Portfolio Limit</div>
                    <div className="scenario-stats">
                      <div className="breakdown-item">
                        <span>Required Stop Loss:</span>
                        <span>${calculateStopLossAdjustment(lossAnalysis, (lossAnalysis.portfolio.optimalMaxLoss / lossAnalysis.metrics.accountSize) * 100).newStopLoss.toFixed(2)}</span>
                      </div>
                      <div className="breakdown-item">
                        <span>Adjustment Needed:</span>
                        <span>{calculateStopLossAdjustment(lossAnalysis, (lossAnalysis.portfolio.optimalMaxLoss / lossAnalysis.metrics.accountSize) * 100).stopLossAdjustment.toFixed(1)}%</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Quick Adjustment Presets */}
                <div className="risk-summary">
                  <h4>Quick Risk Adjustment</h4>
                  <div className="preset-buttons">
                    <button className="preset-btn conservative" onClick={() => {
                      const newStopLoss = calculateStopLossAdjustment(lossAnalysis, parseFloat(inputs.maxSingleLoss) * 0.5).newStopLoss;
                      setInputs(prev => ({ ...prev, stopLossPrice: newStopLoss.toFixed(2) }));
                      addNotification("Stop loss adjusted to conservative level", "info");
                    }}>
                      Conservative (-50%)
                    </button>
                    <button className="preset-btn moderate" onClick={() => {
                      const newStopLoss = calculateStopLossAdjustment(lossAnalysis, parseFloat(inputs.maxSingleLoss)).newStopLoss;
                      setInputs(prev => ({ ...prev, stopLossPrice: newStopLoss.toFixed(2) }));
                      addNotification("Stop loss adjusted to moderate level", "info");
                    }}>
                      Moderate (At Limit)
                    </button>
                    <button className="preset-btn aggressive" onClick={() => {
                      const newPosition = portfolioImpact.recommendedPositionSize;
                      setInputs(prev => ({ ...prev, positionSize: newPosition.toFixed(0) }));
                      addNotification("Position size adjusted to fit risk limits", "info");
                    }}>
                      Adjust Position Size
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      <br/><WaldoAd type="horizontal" />
      {content && content.content}
    </div>
  );
};

export default MaxLossCalculator;